import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import subscriptionService, { SUBSCRIPTION_STATES } from '../services/subscriptionService';
import { useTranslation } from 'react-i18next';

const SubscriptionContext = createContext();

// Costante per il tempo minimo tra gli aggiornamenti forzati (5 minuti)
const MIN_REFRESH_INTERVAL = 5 * 60 * 1000;

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const lastRefreshTime = useRef(0);
  let unsubscribeSubscription = null;

  useEffect(() => {
    // Funzione per recuperare l'abbonamento con aggiornamento ottimizzato
    const fetchSubscription = async (user) => {
      if (!user) {
        setSubscription(null);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Ottieni l'abbonamento corrente e aggiornalo in un'unica operazione
        const { data, refreshed } = await subscriptionService.getWithOptionalRefresh();
        
        setSubscription(data);
        lastRefreshTime.current = Date.now();
        setLoading(false);
      } catch (error) {
        console.error('Errore nel recupero dell\'abbonamento:', error);
        setError('Errore nel recupero dell\'abbonamento. Riprova più tardi.');
        setLoading(false);
      }
    };

    // Configurazione del listener di autenticazione
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      fetchSubscription(user);
      
      // Se l'utente è autenticato, imposta anche il listener per i cambiamenti dell'abbonamento
      if (user) {
        if (unsubscribeSubscription) {
          unsubscribeSubscription();
        }
        
        unsubscribeSubscription = subscriptionService.subscribeToSubscriptionChanges((result) => {
          setSubscription(result.data);
        });
      } else {
        // Se l'utente non è autenticato, annulla i listener esistenti
        if (unsubscribeSubscription) {
          unsubscribeSubscription();
          unsubscribeSubscription = null;
        }
      }
    });
    
    // Pulizia dei listener quando il componente viene smontato
    return () => {
      unsubscribeAuth();
      if (unsubscribeSubscription) {
        unsubscribeSubscription();
      }
    };
  }, []);

  // Funzione per forzare l'aggiornamento dell'abbonamento solo se necessario
  const refreshSubscriptionIfNeeded = async (force = false) => {
    // Verifica se è trascorso abbastanza tempo dall'ultimo aggiornamento
    const now = Date.now();
    if (force || !lastRefreshTime.current || (now - lastRefreshTime.current) > MIN_REFRESH_INTERVAL) {
      try {
        const result = await subscriptionService.forceRefreshSubscription();
        
        // Aggiorna lo stato solo se l'abbonamento è stato effettivamente aggiornato
        if (result.success) {
          if (result.updated) {
            setSubscription(result.data);
          }
          
          // Aggiorna sempre il timestamp dell'ultimo aggiornamento
          lastRefreshTime.current = now;
        }
        
        return result;
      } catch (error) {
        console.error('Errore nell\'aggiornamento forzato dell\'abbonamento:', error);
        throw error;
      }
    } else {
      return { success: true, data: subscription, skipped: true, updated: false };
    }
  };

  // Verifica se l'utente può creare un nuovo dispositivo
  const canCreateDevice = async () => {
    try {
      // Non è più necessario aggiornare l'abbonamento qui
      // poiché il metodo canCreateDevice del servizio già utilizza getWithOptionalRefresh
      const result = await subscriptionService.canCreateDevice();
      
      // Traduci il messaggio di errore se presente
      if (!result.canCreate && result.reason) {
        // Verifica se il percorso dell'errore inizia già con 'settings.'
        const translationKey = result.reason.startsWith('settings.') 
          ? result.reason 
          : `settings.${result.reason}`;
        
        return {
          canCreate: false,
          reason: t(translationKey)
        };
      }
      
      return result;
    } catch (error) {
      console.error('Errore nella verifica della possibilità di creare dispositivi:', error);
      return { canCreate: false, reason: error.message };
    }
  };

  // Incrementa il contatore dei dispositivi
  const incrementDeviceCount = async () => {
    try {
      const result = await subscriptionService.incrementDeviceCount();
      
      // Aggiorna lo stato locale con il nuovo contatore
      if (result && result.data) {
        setSubscription(prevSubscription => ({
          ...prevSubscription,
          devicesCreated: result.data.devicesCreated
        }));
      }
      
      return result;
    } catch (error) {
      console.error('Errore nell\'incremento del contatore dei dispositivi:', error);
      throw error;
    }
  };

  // Verifica se l'utente può creare un nuovo ricambio
  const canCreatePart = async () => {
    try {
      // Non è più necessario aggiornare l'abbonamento qui
      // poiché il metodo canCreatePart del servizio già utilizza getWithOptionalRefresh
      const result = await subscriptionService.canCreatePart();
      
      // Traduci il messaggio di errore se presente
      if (!result.canCreate && result.reason) {
        // Verifica se il percorso dell'errore inizia già con 'settings.'
        const translationKey = result.reason.startsWith('settings.') 
          ? result.reason 
          : `settings.${result.reason}`;
        
        return {
          canCreate: false,
          reason: t(translationKey)
        };
      }
      
      return result;
    } catch (error) {
      console.error('Errore nella verifica della possibilità di creare ricambi:', error);
      return { canCreate: false, reason: error.message };
    }
  };

  // Incrementa il contatore dei ricambi
  const incrementPartCount = async () => {
    try {
      const result = await subscriptionService.incrementPartCount();
      
      // Aggiorna lo stato locale con il nuovo contatore
      if (result && result.data) {
        setSubscription(prevSubscription => ({
          ...prevSubscription,
          partsCreated: result.data.partsCreated
        }));
      }
      
      return result;
    } catch (error) {
      console.error('Errore nell\'incremento del contatore dei ricambi:', error);
      throw error;
    }
  };

  // Aggiorna l'abbonamento a pagamento
  const upgradeToPaid = async (planDetails) => {
    try {
      const result = await subscriptionService.upgradeToPaid(planDetails);
      return result;
    } catch (error) {
      console.error('Errore nell\'aggiornamento dell\'abbonamento:', error);
      throw error;
    }
  };

  // Rinnova l'abbonamento
  const renewSubscription = async () => {
    try {
      const result = await subscriptionService.renewSubscription();
      return result;
    } catch (error) {
      console.error('Errore nel rinnovo dell\'abbonamento:', error);
      throw error;
    }
  };

  // Verifica se l'abbonamento è attivo
  const isSubscriptionActive = () => {
    if (!subscription) return false;
    const status = subscriptionService.getSubscriptionStatus(subscription);
    return status.isActive;
  };

  // Verifica se l'abbonamento è scaduto e aggiornalo se necessario
  const checkAndUpdateExpiredSubscription = async () => {
    if (!subscription || !auth.currentUser) {
      return false;
    }

    try {
      const result = await subscriptionService.checkAndUpdateExpiredSubscription(subscription);
      if (result.updated) {
        setSubscription(result.data);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Errore nell\'aggiornamento dello stato dell\'abbonamento:', err);
      return false;
    }
  };

  // Verifica se l'utente ha raggiunto il limite di dispositivi
  const hasReachedDeviceLimit = () => {
    if (!subscription) {
      return true;
    }

    const status = subscriptionService.getSubscriptionStatus(subscription);
    return status.deviceLimitReached;
  };

  // Verifica se l'utente ha raggiunto il limite di ricambi
  const hasReachedPartLimit = () => {
    if (!subscription) {
      return true;
    }

    const status = subscriptionService.getSubscriptionStatus(subscription);
    return status.partLimitReached;
  };

  // Ottieni il numero di dispositivi rimanenti
  const getRemainingDevices = () => {
    if (!subscription) {
      return 0;
    }

    const status = subscriptionService.getSubscriptionStatus(subscription);
    return status.remainingDevices;
  };

  // Ottieni il numero di ricambi rimanenti
  const getRemainingParts = () => {
    if (!subscription) {
      return 0;
    }

    const status = subscriptionService.getSubscriptionStatus(subscription);
    return status.remainingParts;
  };

  const value = {
    subscription,
    loading,
    error,
    canCreateDevice,
    incrementDeviceCount,
    canCreatePart,
    incrementPartCount,
    upgradeToPaid,
    renewSubscription,
    isSubscriptionActive,
    checkAndUpdateExpiredSubscription,
    hasReachedDeviceLimit,
    hasReachedPartLimit,
    getRemainingDevices,
    getRemainingParts,
    refreshSubscription: (force = true) => refreshSubscriptionIfNeeded(force)
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
} 